import type { ApiError, TokenPair } from '@bk/contracts';

const REFRESH_KEY = 'bk.refresh';
let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError,
  ) {
    super(body.message);
  }
}

/** Sessione: token di accesso solo in memoria, refresh token nel browser (ruotato a ogni rinnovo). */
export const session = {
  store(pair: TokenPair) {
    accessToken = pair.access_token;
    try {
      localStorage.setItem(REFRESH_KEY, pair.refresh_token);
    } catch {
      /* storage non disponibile: la sessione dura quanto la scheda */
    }
  },
  clear() {
    accessToken = null;
    try {
      localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* ignorato */
    }
  },
  hasRefresh(): boolean {
    try {
      return Boolean(localStorage.getItem(REFRESH_KEY));
    } catch {
      return false;
    }
  },
};

const storedRefresh = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
};

/** Un rinnovo alla volta anche tra le schede dello stesso browser (ADR 0146): il refresh token ruota a ogni rinnovo. */
function oneAtATime<T>(work: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks ? (locks.request('bk-refresh', work) as Promise<T>) : work();
}

const pause = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function refresh(): Promise<boolean> {
  return oneAtATime(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      const token = storedRefresh();
      if (!token) return false;
      let res: Response;
      try {
        res = await fetch('/api/v1/auth/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: token }) });
      } catch {
        // Rete assente un attimo: si riprova, la sessione resta.
        await pause(800 * (attempt + 1));
        continue;
      }
      if (res.ok) {
        session.store((await res.json()) as TokenPair);
        return true;
      }
      // Un'altra scheda ha appena ruotato il token: si riprova con quello nuovo, senza uscire.
      if ([400, 401, 403].includes(res.status)) {
        if (storedRefresh() !== token) continue;
        session.clear();
        return false;
      }
      // Solo un rifiuto del token chiude la sessione: un limite di richieste o un server occupato non fanno uscire (ADR 0051).
      await pause(800 * (attempt + 1));
    }
    return false;
  });
}

/** La sessione c'è ma il rinnovo non è riuscito per ora: la richiesta non parte senza token (ADR 0146). */
const notYet = () =>
  new ApiRequestError(503, { code: 'session_retry', message: 'Sto ricollegando la sessione: riprova tra un attimo.' } as ApiError);

/** Chiamata all'API con rinnovo automatico del token una sola volta per richiesta. */
export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}, retried = false): Promise<T> {
  if (!accessToken && !retried && session.hasRefresh()) {
    refreshing ??= refresh().finally(() => (refreshing = null));
    await refreshing;
    if (!accessToken && session.hasRefresh()) throw notYet();
  }
  const res = await fetch(`/api/v1${path}`, {
    method: init.method ?? (init.body === undefined ? 'GET' : 'POST'),
    headers: { ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  if (res.status === 401 && !retried && session.hasRefresh() && !path.startsWith('/auth/login')) {
    refreshing ??= refresh().finally(() => (refreshing = null));
    if (await refreshing) return api<T>(path, init, true);
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => ({ code: 'invalid_response', message: 'Risposta non valida dal server' }));
  if (!res.ok) throw new ApiRequestError(res.status, body as ApiError);
  return body as T;
}

export const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : 'Errore imprevisto');

/** Upload di un file (multipart) con la stessa gestione di sessione ed errori di `api`. */
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  if (!accessToken && session.hasRefresh()) {
    refreshing ??= refresh().finally(() => (refreshing = null));
    await refreshing;
  }
  const form = new FormData();
  form.append('file', file);
  const send = () => fetch(`/api/v1${path}`, { method: 'POST', headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}, body: form });
  let res = await send();
  if (res.status === 401 && session.hasRefresh()) {
    refreshing ??= refresh().finally(() => (refreshing = null));
    if (await refreshing) res = await send();
  }
  const body = await res.json().catch(() => ({ code: 'invalid_response', message: 'Risposta non valida dal server' }));
  if (!res.ok) throw new ApiRequestError(res.status, body as ApiError);
  return body as T;
}

/** Scarica un file dall'API (con la sessione) e lo fa salvare al browser con il nome dato dal server. */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  if (!accessToken && session.hasRefresh()) {
    refreshing ??= refresh().finally(() => (refreshing = null));
    await refreshing;
  }
  const send = () => fetch(`/api/v1${path}`, { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
  let res = await send();
  if (res.status === 401 && session.hasRefresh()) {
    refreshing ??= refresh().finally(() => (refreshing = null));
    if (await refreshing) res = await send();
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ code: 'invalid_response', message: 'Download non riuscito' }));
    throw new ApiRequestError(res.status, body as ApiError);
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const name = encoded ? decodeURIComponent(encoded) : fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Un file dell'API (con la sessione) come indirizzo locale da mostrare, es. un'immagine; null se non c'è. */
export async function apiObjectUrl(path: string): Promise<string | null> {
  if (!accessToken && session.hasRefresh()) {
    refreshing ??= refresh().finally(() => (refreshing = null));
    await refreshing;
  }
  const send = () => fetch(`/api/v1${path}`, { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
  let res = await send();
  if (res.status === 401 && session.hasRefresh()) {
    refreshing ??= refresh().finally(() => (refreshing = null));
    if (await refreshing) res = await send();
  }
  return res.ok ? URL.createObjectURL(await res.blob()) : null;
}
